import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import FlowWrapper from '@/Utils/flowWrapper';
import { UploadStatus } from '@/Types/Enums/uploadStatus';
import UploadModel from '@/Models/uploadModel';
import { FileEventData, FileProgressEventData, FileUploadEvent } from '@publicsafety/fileupload';
import uploadService from '@/Services/uploadService';
import logger from '@/Utils/logger';

export const useUploadManagerStore = defineStore('uploadManager', () => {
  const uploadCancelledCallbacks = [];

  // State
  const uploads = ref<UploadModel[]>([]);
  const currentOverallProgress = ref<number>(100);
  const completedUploadsCounter = ref(0);
  const failedUploadsCounter = ref(0);

  // Getters
  const uploadsCount = computed(() => uploads.value.length);
  const allUploadsCompleted = computed(() => !uploads.value.length);
  const anyUploadsFailed = computed(() => !!failedUploadsCounter.value);

  // Actions
  function removeUpload(upload: UploadModel): void {

    const index = uploads.value.indexOf(upload);
    uploads.value.splice(index, 1);

    if (uploadsCount.value === 0) {
      currentOverallProgress.value = 0;
    }
  }

  function addUpload(upload: UploadModel): void {
    uploads.value.push(upload);
    FlowWrapper.addFileUpload(upload);
  }

  function getUploadById(id: string): UploadModel | undefined {
    return uploads.value.find((u: UploadModel) => u.uploadId === id);
  }

  function getUploadTokenById(id: string): string | undefined {
    const upload = getUploadById(id);
    return upload?.uploadToken;
  }

  function cancelUpload(upload: UploadModel): void {
    if (upload.nativeBrowserFile) {
      FlowWrapper.cancelFileUpload(upload.nativeBrowserFile);
    }

    setUploadCancelled(upload, uploadCancelledCallbacks);
    removeUpload(upload);
  }

  function onUploadCancelled(callback: (upload: UploadModel) => void) {
    uploadCancelledCallbacks.push(callback);

    return () => {
      uploadCancelledCallbacks.splice(uploadCancelledCallbacks.indexOf(callback), 1);
    };
  }

  // FlowWrapper subscriptions
  FlowWrapper.subscribeTo(FileUploadEvent.FileStarted, (data: FileEventData) => {
    const upload = getUploadById(data.id);

    // If an upload file was already removed
    // don't do anything.
    if (!upload) {
      return;
    }

    setUploadStarted(upload);
  });

  FlowWrapper.subscribeTo(FileUploadEvent.FileSuccess, (data: FileEventData) => {
    const upload = getUploadById(data.id);
    if (!upload) {
      return;
    }

    uploadService.uploadComplete(upload.uploadToken)
      .then(resultDto => {
        if (resultDto.uploadStatusName === UploadStatus.Uploaded) {

          setUploadCompleted(upload);
          completedUploadsCounter.value++;
          return;
        }

        logger.error('FailedToCompleteUpload');
        setFailed(upload);
      })
      .catch(() => {
        logger.error('FailedToCompleteUpload');
        setFailed(upload);
      })
      .finally(() => {
        removeUpload(upload);
      });
  });

  FlowWrapper.subscribeTo(FileUploadEvent.FileError, (data: FileEventData) => {
    const upload = getUploadById(data.id);

    // If an upload file was already removed
    // don't do anything.
    if (!upload) {
      return;
    }

    // If the upload was cancelled, then we know about this ajax failure and can ignore it. Only if the
    // upload wasn't cancelled display the upload failure message.
    if (upload.statusName === UploadStatus.Cancelled) {
      return;
    }

    setFailed(upload);

    removeUpload(upload);

    const errorMessage = 'Filename: ' + upload.fileName;
    logger.errorWithoutTimeout('UploadFailure', errorMessage);
  });

  FlowWrapper.subscribeTo(FileUploadEvent.FileProgress, (data: FileProgressEventData) => {
    const upload = getUploadById(data.id);

    // If a file was removed but a chunk finished uploading after the Ajax request was cancelled then
    // don't do anything.
    if (!upload) {
      return;
    }

    updateUploadProgress(upload, data.percentage);

    currentOverallProgress.value = data.overallPercentage;
  });

  async function setFailed(upload: UploadModel) {
    upload.statusName = UploadStatus.Failed;
    failedUploadsCounter.value++;

    await uploadService.setUploadFailed(upload.uploadToken);
  }

  return {
    uploads,
    uploadsCount,
    completedUploadsCounter,
    currentOverallProgress,
    failedUploadsCounter,
    addUpload,
    getUploadTokenById,
    cancelUpload,
    onUploadCancelled,
    allUploadsCompleted,
    anyUploadsFailed,
    getUploadById
  };
});

function setUploadCompleted(upload: UploadModel) {
  upload.statusName = UploadStatus.Uploaded;
  upload.progress = 100;
  upload.bytesUploaded = upload.size;
}

function updateUploadProgress(upload: UploadModel, percentage: number) {
  upload.progress = percentage;
  upload.bytesUploaded = upload.size * percentage * 0.01;
}

function setUploadStarted(upload: UploadModel) {
  upload.statusName = UploadStatus.Uploading;
  upload.progress = 0;
}

function setUploadCancelled(upload: UploadModel, callbacks?: []) {
  upload.statusName = UploadStatus.Cancelled;

  if (!callbacks?.length) {
    return;
  }

  for (const callback of callbacks) {
    callback(upload);
  }
}